import type { BinaryStore } from '../core/api/client.js';

/**
 * Audio bytes, cached in the page by conversation id.
 *
 * Findings S1 and S2 together. `GET /api/conversations/{id}/audio` redirects to a Blob SAS
 * that expires in **15 minutes**, and nothing cached the bytes:
 *   - replaying a call re-downloaded several megabytes every time
 *   - a call longer than the SAS lifetime simply STOPPED mid-playback, which is a defect
 *     rather than a polish item
 *
 * Fetching the file once and playing from an object URL removes both at a stroke. Keyed by
 * conversation id, never by URL — the URL rotates every 15 minutes, the bytes never change.
 * Recordings are immutable once synced, which makes this trivially safe.
 *
 * In memory on purpose for now: object URLs die with the tab anyway, and putting call audio
 * into IndexedDB is a real decision about how much of someone's disk Fig may use, and for how
 * long. That belongs in front of Rasel rather than defaulted quietly.
 */
class ObjectUrlAudioStore implements BinaryStore {
  #urls = new Map<string, string>();

  async get(key: string): Promise<string | null> {
    return this.#urls.get(key) ?? null;
  }

  async put(key: string, data: ArrayBuffer, contentType: string): Promise<string> {
    const existing = this.#urls.get(key);
    if (existing) URL.revokeObjectURL(existing);
    const url = URL.createObjectURL(new Blob([data], { type: contentType }));
    this.#urls.set(key, url);
    return url;
  }

  /** Release every object URL. Recordings must not outlive the session allowed to hear them. */
  clear(): void {
    for (const url of this.#urls.values()) URL.revokeObjectURL(url);
    this.#urls.clear();
  }
}

export const audioStore = new ObjectUrlAudioStore();
